/* CostCentersCrudCard.tsx
	Une card affichant la gestion CRUD des centres de coûts
	Utilise le composant GenericCrudCard pour lister, ajouter, éditer et supprimer les éléments
	Utilise listPaginated pour bénéficier de la pagination, du tri et des filtres côté serveur
*/

// Import React
import React from 'react';

// Import FontAwesome
import { faCube } from '@fortawesome/free-solid-svg-icons';
import { IconDefinition } from '@fortawesome/fontawesome-svg-core';

// Import Communs
import { GenericCrudCard } from 'common';

// Import Feature
import { CostCenterData, costCentersApi } from 'features/costCenter';

// Interface des props du composant
interface CostCentersCrudCardProps {
	size?: 'sm' | 'md' | 'lg';
	returnPath?: string;
	returnLabel?: string;
	returnIcon?: IconDefinition;
	returnInvert?: boolean;
	className?: string;
	/** Affiche ou masque l'icône dans l'en-tête de la card. Défaut : true */
	showIcon?: boolean;
}

// CostCentersCrudCard component
export const CostCentersCrudCard: React.FC<CostCentersCrudCardProps> = ({
	size = 'sm',
	returnPath = '/app/settings/depenses',
	returnLabel,
	returnIcon,
	returnInvert = false,
	className,
	showIcon = true,
}) => {
	// Détermination si on utilise la version étendue (size !== 'sm')
	const extended = size !== 'sm';
	// Titre de la card selon la taille
	const cardTitle =
		size === 'sm' ? 'Centres de coûts' : 'Liste des centres de coûts';

	// Définition des colonnes du tableau
	const columns = [
		// Colonne "ID" uniquement si extended
		...(extended
			? [{ label: 'ID', field: 'id', render: (a: CostCenterData) => a.id }]
			: []),
		// Colonne "Exercice"
		{
			label: 'Exercice',
			field: 'exercise',
			render: (a: CostCenterData) => a.exercise?.toString() ?? '—',
		},
		// Colonne "Catégorie" avec tri sur le label de la relation
		{
			label: 'Catégorie',
			field: 'categorie.label',
			render: (a: CostCenterData) => a.categorie?.label ?? '—',
		},
		// Colonne "Libellé"
		{
			label: 'Libellé',
			field: 'label',
			render: (a: CostCenterData) => a.label,
		},
		// Colonne "Code"
		{
			label: 'Code',
			field: 'code',
			render: (a: CostCenterData) => a.code ?? '—',
		},
	];

	return (
		<GenericCrudCard<CostCenterData>
			queryKey="costCenters"
			title={cardTitle}
			fetchData={(token, options) =>
				costCentersApi.listPaginated(token, options)
			}
			deleteItem={costCentersApi.delete}
			tableColumns={columns}
			addButtonLabel="Ajouter un centre de coûts"
			getAddPath={() => '/app/settings/cost-centers/new'}
			getEditPath={(id) => `/app/settings/cost-centers/${id}/edit`}
			emptyMessage="Aucun centre de coûts trouvé."
			getReturnPath={() => returnPath}
			returnLabel={returnLabel}
			returnIcon={returnIcon}
			returnInvert={returnInvert}
			size={size}
			icon={size === 'sm' ? faCube : undefined}
			className={className}
			showIcon={showIcon}
		/>
	);
};

export default CostCentersCrudCard;
